import React from "react";
import { AxiosError } from "axios";
import { ConfirmationModal } from "@packsize/components";
import { FormattedMessage, useIntl } from "react-intl";
import { IconButton, Typography } from "@mui/material";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";

import { type PickZone } from "@/schemas";
import { useDeletePickZone } from "@/hooks/pick-zones";

interface PickZoneDeleteButtonProps {
	readonly pickZone: PickZone;
}

export const PickZoneDeleteButton: React.FC<PickZoneDeleteButtonProps> = ({ pickZone }) => {
	const intl = useIntl();

	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

	const { mutate, isPending, error } = useDeletePickZone(pickZone.id);

	const handleDeleteConfirmed = () => {
		mutate(undefined, {
			onSuccess: () => {
				setShowDeleteConfirmation(false);
			},
			onError: (error) => {
				if (error instanceof AxiosError && error.status === 404) {
					setShowDeleteConfirmation(false);
				}
			},
		});
	};

	return (
		<>
			<ConfirmationModal
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				onCancel={() => setShowDeleteConfirmation(false)}
				onClose={() => setShowDeleteConfirmation(false)}
				onConfirm={handleDeleteConfirmed}
				open={showDeleteConfirmation}
				size="sm"
				title={intl.formatMessage(
					{
						id: "Delete {alias}?",
						defaultMessage: "Delete {alias}?",
					},
					{
						alias: pickZone.alias,
					},
				)}
				slotProps={{
					confirmButton: {
						"data-testid": "pick-zone-delete-confirmation-modal__button__confirm",
						loading: isPending,
					},
					cancelButton: {
						"data-testid": "pick-zone-delete-confirmation-modal__button__cancel",
					},
				}}
			>
				<Typography>
					<FormattedMessage
						id="Are you sure you want to delete this pick zone?"
						defaultMessage="Are you sure you want to delete this pick zone?"
					/>
				</Typography>
				<Typography>
					<FormattedMessage id="This action cannot be undone." defaultMessage="This action cannot be undone." />
				</Typography>

				{Boolean(error) && (
					<Typography sx={{ marginTop: 2 }} color="error" data-testid="pick-zone-delete-confirmation-modal__error">
						<FormattedMessage
							id="Failed to delete pick zone. Please try again."
							defaultMessage="Failed to delete pick zone. Please try again."
						/>
					</Typography>
				)}
			</ConfirmationModal>
			<IconButton
				onClick={() => setShowDeleteConfirmation(true)}
				data-testid="pick-zone-delete-button__button__open-modal"
			>
				<DeleteOutlineRounded />
			</IconButton>
		</>
	);
};
